import { CreateSchedulePayload, RateUnit, Schedule, UpdateSchedulePayload } from "@circuitry-ai/doc-data";
import type { TargetType } from 'modules/shared/data/src/features/settings/settingstypes';
import { ScheduleFormData } from "./scheduler-settings";
export const getSchedules = (schedulesData?: Schedule[]) => {
    if (Array.isArray(schedulesData)) {
      return schedulesData;
    }
    if (schedulesData && typeof schedulesData === 'object') {
      // Handle various response structures
      const data = schedulesData as Record<string, unknown>;
      if (Array.isArray(data.items)) return data.items as Schedule[];
      if (Array.isArray(data.schedules)) return data.schedules as Schedule[];
      if (Array.isArray(data.list)) return data.list as Schedule[];
      if (Array.isArray(data.data)) return data.data as Schedule[];
    }
    return [];
  }

  export const TIMEZONE_OPTIONS = [
    { key: 'UTC', value: 'UTC', label: 'UTC' },
    { key: 'America/New_York', value: 'America/New_York', label: 'America/New_York' },
    { key: 'America/Los_Angeles', value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
    { key: 'America/Chicago', value: 'America/Chicago', label: 'America/Chicago' },
    { key: 'Europe/London', value: 'Europe/London', label: 'Europe/London' },
    { key: 'Europe/Paris', value: 'Europe/Paris', label: 'Europe/Paris' },
    { key: 'Asia/Tokyo', value: 'Asia/Tokyo', label: 'Asia/Tokyo' },
    { key: 'Asia/Kolkata', value: 'Asia/Kolkata', label: 'Asia/Kolkata' },
  ] as const;

  export const STATE_OPTIONS = [
    { key: 'ENABLED', value: 'ENABLED', labelKey: 'Enabled' },
    { key: 'DISABLED', value: 'DISABLED', labelKey: 'Disabled' },
  ] as const;
  
  export const RATE_UNIT_OPTIONS = [
    { key: 'minutes', value: 'minutes', labelKey: 'Minutes' },
    { key: 'hours', value: 'hours', labelKey: 'Hours' },
    { key: 'days', value: 'days', labelKey: 'Days' },
  ] as const;
    
  export const getScheduleTypeClass = (scheduleType: string): string => {
    // Handle both simplified_type values ("Rate", "Recurring", "One-time") and scheduleTypeOptions
    if (scheduleType === scheduleTypeOptions.RATE || scheduleType === 'Rate' || scheduleType === 'RATE') {
      return 'bg-primary/10 text-primary';
    }
    if (scheduleType === scheduleTypeOptions.Recurring || scheduleType === 'Recurring' || scheduleType === 'CRON') {
      return 'bg-purple-100 text-purple-700';
    }
    return 'bg-warning/10 text-warning';
  };
  
  export const getScheduleStateClass = (state: string): string => {
    if (state === 'ENABLED') {
      return 'bg-success/10 text-success';
    }
    return 'bg-default-100 text-default-500';
  };
  
  export const formatAtScheduleTime = (scheduleAt: string): string | null => {
    const trimmed = scheduleAt?.trim();
    if (!trimmed) {
      return null;
    }
    const atTime = trimmed.includes('.') ? trimmed.split('.')[0] : trimmed;
    const formattedTime = atTime.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/) ? `${atTime}:00` : atTime;
    return formattedTime;
  };
  
  export const buildRatePayload = (data: ScheduleFormData) => ({
    value: data.rateValue || 5,
    unit: (data.rateUnit || 'minutes') as RateUnit
  });
  
 export const formatScheduleExpression = (schedule: Schedule): string => {
    if ((schedule as any).schedule_expression) {
      return (schedule as any).schedule_expression;
    }
    if (schedule.schedule_type === scheduleTypeOptions.RATE && schedule.schedule_rate) {
      return `rate(${schedule.schedule_rate.value} ${schedule.schedule_rate.unit})`;
    }
    if (schedule.schedule_type === scheduleTypeOptions.One_time && schedule.schedule_at) {
      return `at(${schedule.schedule_at})`;
    }
    return 'N/A';
  };
  
  export const scheduleTypeOptions ={
    RATE: 'Rate',
    Recurring: 'Recurring',
    One_time: 'One-time',
  } as const;

  export const formatTime12h = (time24h: string): string => {
    if (!time24h) return '';
    const [hours, minutes] = time24h.split(':');
    if (!hours || !minutes) return '';
    const hour24 = Number.parseInt(hours, 10);
    if (Number.isNaN(hour24)) return '';
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'pm' : 'am';
    return `${hour12}:${minutes.padStart(2, '0')} ${ampm}`;
  };

  export const parseTime12h = (time12h: string): string => {
    if (!time12h) return '';
    // Remove any extra spaces and normalize
    const normalized = time12h.trim().toLowerCase();
    // Match patterns like "4:00 pm", "04:00pm", "4:0 pm", etc.
    // Use bounded quantifiers to prevent ReDoS: hours (1-2 digits), minutes (1-2 digits)
    const match = normalized.match(/^(\d{1,2}):(\d{1,2})\s*(am|pm)$/);
    if (match) {
      let hour = Number.parseInt(match[1], 10);
      const minutes = match[2].padStart(2, '0');
      const ampm = match[3];
      if (ampm === 'pm' && hour !== 12) hour += 12;
      if (ampm === 'am' && hour === 12) hour = 0;
      return `${hour.toString().padStart(2, '0')}:${minutes}`;
    }
    // If no match, try to return as-is if it's already in 24h format
    if (normalized.match(/^\d{1,2}:\d{2}$/)) {
      return normalized;
    }
    return '';
  };

  export const buildRateSchedulePattern = (data: ScheduleFormData) => {
    return {
      type: 'RATE' as const,
      value: data.rateValue || 1,
      unit: (data.rateUnit || 'hours') as RateUnit
    };
  };

  export const buildOneTimeSchedulePattern = (data: ScheduleFormData) => {
    if (!data.oneTimeDate || !data.oneTimeTime) {
      return null;
    }
    // Time is already in 24-hour format from the time input
    return {
      type: 'ONE-TIME' as const,
      date: data.oneTimeDate,
      time: data.oneTimeTime // Already in 24h format (HH:MM)
    };
  };

  export const buildRecurringSchedulePattern = (data: ScheduleFormData) => {
    if (!data.recurringFrequency || !data.recurringStartTime) {
      return null;
    }
    
    const pattern: {
      type: string;
      time: string; // Already in 24h format (HH:MM)
      days?: string[];
      day_of_month?: number | 'l';
    } = {
      type: data.recurringFrequency,
      time: data.recurringStartTime // Already in 24h format
    };
    
    if (data.recurringFrequency === 'WEEKLY' && data.recurringDays && data.recurringDays.length > 0) {
      pattern.days = data.recurringDays;
    } else if (data.recurringFrequency === 'MONTHLY' && data.recurringDayOfMonth !== undefined) {
      pattern.day_of_month = data.recurringDayOfMonth === 'l' ? 'l' : Number(data.recurringDayOfMonth);
    }
    
    return pattern;
  };

  export const buildRecurringDateFields = (data: ScheduleFormData) => {
    const dateFields: { start_date?: string; end_date?: string | null } = {};
    
    // Add start_date for DAILY and WEEKLY
    if ((data.recurringFrequency === 'DAILY' || data.recurringFrequency === 'WEEKLY') && data.recurringStartDate) {
      dateFields.start_date = data.recurringStartDate;
    }
    
    // Add end_date if end condition is specific_date, otherwise set to null for "never"
    if (data.recurringEndCondition === 'specific_date' && data.recurringEndDate) {
      dateFields.end_date = `${data.recurringEndDate}T23:59:00`;
    } else if (data.recurringEndCondition === 'never') {
      dateFields.end_date = null;
    }
    
    return dateFields;
  };

  export const buildRateDateFields = (data: ScheduleFormData) => {
    const dateFields: { start_date?: string; end_date?: string | null } = {};
    if (data.rateStartDate) {
      dateFields.start_date = data.rateStartDate;
    }
    if (data.rateEndCondition === 'specific_date' && data.rateEndDate) {
      dateFields.end_date = `${data.rateEndDate}T23:59:00`;
    } else if (data.rateEndCondition === 'never') {
      dateFields.end_date = null;
    }
    return dateFields;
  };

  export const buildSchedulePattern = (data: ScheduleFormData) => {
    if (data.schedule_type === scheduleTypeOptions.RATE) {
      return buildRateSchedulePattern(data);
    } else if (data.schedule_type === scheduleTypeOptions.One_time) {
      return buildOneTimeSchedulePattern(data);
    } else if (data.schedule_type === scheduleTypeOptions.Recurring) {
      return buildRecurringSchedulePattern(data);
    }
    return null;
  };

  export const craftSchedulePayload = (data: ScheduleFormData) => {
    const payload: UpdateSchedulePayload = {
        target_id: data.target_id,
        state: data.state,
        schedule_timezone: data.timezone,
      };

      if (data.description) {
        payload.description = data.description;
      }
      if (data.schedule_type === scheduleTypeOptions.Recurring) {
        payload.schedule_type = scheduleTypeOptions.Recurring;
      } else {
        payload.schedule_type = data.schedule_type;
      }
      return payload;
  }

  /** Local calendar date YYYY-MM-DD; use as `min` on schedule date inputs to block past days. */
  export const getScheduleDateInputMin = (): string => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  export const parseDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const str = String(dateStr);
    // Handle compact yyyyMMdd format
    if (/^\d{8}$/.test(str)) {
      return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`;
    }
    // Handle both "2026-01-07T00:00:00" and "2026-01-07 00:00:00" formats
    if (str.includes('T')) {
      return str.split('T')[0] || '';
    } else if (str.includes(' ')) {
      return str.split(' ')[0] || '';
    }
    return str;
  };

  export const getFilteredSchedules = (schedules: Schedule[], searchTerm: string, stateFilter: string, typeFilter: string): Schedule[] => {
    return schedules.filter((schedule: Schedule) => {
      const scheduleAny = schedule as any;
      const simplifiedType = scheduleAny.simplified_type || schedule.schedule_type;
      const matchesSearch = 
        schedule.schedule_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        schedule.target_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (schedule.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesState = !stateFilter || schedule.state === stateFilter;
      // Map simplified_type to filter values: "Rate" -> "RATE", "Recurring" -> "CRON", "One-time" -> "AT"
      const typeForFilter = simplifiedType === 'Rate' ? 'RATE' : 
                           simplifiedType === 'Recurring' ? 'CRON' : 
                           simplifiedType === 'One-time' ? 'AT' : simplifiedType;
      const matchesType = !typeFilter || typeForFilter === typeFilter;
      return matchesSearch && matchesState && matchesType;
    });
  };

  export const getBuildSchedulePayload = (data: ScheduleFormData): CreateSchedulePayload => {
    const payload: CreateSchedulePayload = {
      schedule_name: data.schedule_name,
      target_id: data.target_id,
      target_type: data.target_type as TargetType,
      schedule_type: data.schedule_type,
      state: data.state,
      schedule_timezone: data.timezone,
    };

    if (data.description) {
      payload.description = data.description;
    }

    return payload;
  };

  export const validateAndParseJson = (json: string): { valid: boolean; parsed?: Record<string, any> } => {
    if (!json.trim()) return { valid: true };
    try {
      const parsed = JSON.parse(json);
      if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
        return { valid: false };
      }
      return { valid: true, parsed };
    } catch {
      return { valid: false };
    }
  };

  export const applyCustomParamsToPayload = (
    payload: any,
    data: { credential_id?: string; custom_params?: Array<{ key: string; value: string }> },
    customParamMode: 'key_value' | 'object',
    customParamJson: string,
  ): { error?: string } => {
    if (data.credential_id) {
      payload.credential_id = data.credential_id;
    }

    if (customParamMode === 'object') {
      if (customParamJson.trim()) {
        const { valid, parsed } = validateAndParseJson(customParamJson);
        if (!valid) {
          return { error: 'invalid_json' };
        }
        if (parsed && Object.keys(parsed).length > 0) {
          payload.custom_params = parsed;
        }
      }
    } else {
      const validCustomParams = processCustomParams(data.custom_params);
      if (Object.keys(validCustomParams).length > 0) {
        payload.custom_params = validCustomParams;
      }
    }

    payload.custom_params_key_type = customParamMode === 'object' ? 'object' : 'key_pair';

    return {};
  };

  export const processCustomParams = (customParams?: Array<{ key: string; value: string }>): Record<string, string> => {
    if (!customParams) return {};
    return customParams
      .filter(param => param.key && param.value)
      .reduce((acc, param) => {
        acc[param.key] = param.value;
        return acc;
      }, {} as Record<string, string>);
  };

  export type AddSchedulePatternToPayloadOptions = {
    /** When true, optional cleared fields (e.g. start_date) are sent as `null` so the backend can clear them. */
    isUpdate?: boolean;
  };

  export const addSchedulePatternToPayload = (
    payload: any,
    data: ScheduleFormData,
    options?: AddSchedulePatternToPayloadOptions,
  ): void => {
    const isUpdate = options?.isUpdate === true;
    // Add schedule_pattern for Rate, One-time, and Recurring using utility functions
    // All times are kept in 24-hour format (HH:MM)
    const schedulePattern = buildSchedulePattern(data);
    if (schedulePattern) {
      payload.schedule_pattern = schedulePattern;
    }
    
    // Add date fields for recurring schedules
    if (data.schedule_type === scheduleTypeOptions.Recurring) {
      const dateFields = buildRecurringDateFields(data);
      const hasOptionalRecurringStartDate =
        data.recurringFrequency === 'DAILY' || data.recurringFrequency === 'WEEKLY';

      if (isUpdate && hasOptionalRecurringStartDate) {
        payload.start_date = data.recurringStartDate?.trim()
          ? dateFields.start_date ?? null
          : null;
      } else if (dateFields.start_date) {
        payload.start_date = dateFields.start_date;
      }

      // Explicitly set end_date to null when "never ends" is selected
      if (dateFields.end_date !== undefined) {
        payload.end_date = dateFields.end_date;
      }
      // DAILY/WEEKLY: optional start_date — start_time only when start_date is set. MONTHLY: no start_date field.
      if (hasOptionalRecurringStartDate) {
        payload.start_time = data.recurringStartDate?.trim()
          ? (data.recurringStartTime || null)
          : null;
      } else {
        payload.start_time = data.recurringStartTime || null;
      }
    }
    // Add optional start_date and end_date for rate schedules
    if (data.schedule_type === scheduleTypeOptions.RATE) {
      const rateDateFields = buildRateDateFields(data);
      if (isUpdate) {
        payload.start_date = data.rateStartDate?.trim()
          ? rateDateFields.start_date ?? null
          : null;
      } else if (rateDateFields.start_date) {
        payload.start_date = rateDateFields.start_date;
      }
      if (rateDateFields.end_date !== undefined) {
        payload.end_date = rateDateFields.end_date;
      }
      payload.start_time = data.rateStartDate?.trim()
        ? (data.rateStartTime || null)
        : null;
    }
  };

  export const formatTime24hTo12h = (time24h: string): string => {
    if (!time24h) return '';
    const [hours, minutes] = time24h.split(':');
    if (!hours || !minutes) return '';
    const hour24 = Number.parseInt(hours, 10);
    if (Number.isNaN(hour24)) return '';
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'pm' : 'am';
    return `${hour12}:${minutes}${ampm}`;
  };

  export const formatDateReadable = (dateStr: string): string => {
    if (!dateStr) return '';
    const dateObj = new Date(dateStr + 'T00:00:00');
    return dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getRateUnitLabel = (unit: string, t: (key: string) => string): string => {
    if (unit === 'hours') return t('hours');
    if (unit === 'minutes') return t('minutes');
    return t('days');
  };

  const generateRatePreviewText = (data: ScheduleFormData, t: (key: string) => string): string => {
    const unit = data.rateUnit || 'hours';
    const value = data.rateValue || 1;
    const unitLabel = getRateUnitLabel(unit, t);
    let text = `${t('Agent will execute every')} ${value} ${unitLabel}`;
    if (data.rateStartDate) {
      text += ` ${t('starting')} ${formatDateReadable(data.rateStartDate)}`;
    }
    if (data.rateEndCondition === 'specific_date' && data.rateEndDate) {
      text += ` ${t('until')} ${formatDateReadable(data.rateEndDate)}`;
    }
    return text;
  };

  const generateOneTimePreviewText = (data: ScheduleFormData, t: (key: string) => string): string => {
    const date = data.oneTimeDate;
    const time = data.oneTimeTime;
    const timezone = data.timezone || 'UTC';
    
    if (!date || !time) {
      return t('Agent will execute once on selected date and time');
    }
    
    const formattedDate = formatDateReadable(date);
    const formattedTime = formatTime24hTo12h(time);
    
    return `${t('Agent will execute once on')} ${formattedDate} ${t('at')} ${formattedTime} (${timezone})`;
  };

  const getDayNamesMap = (t: (key: string) => string): Record<string, string> => {
    return {
      'MON': t('Monday'),
      'TUE': t('Tuesday'),
      'WED': t('Wednesday'),
      'THU': t('Thursday'),
      'FRI': t('Friday'),
      'SAT': t('Saturday'),
      'SUN': t('Sunday')
    };
  };

  const formatStartingDateText = (formattedDate: string, t: (key: string) => string): string => {
    return formattedDate ? ` ${t('starting')} ${formattedDate}` : '';
  };

  const generateDailyPreviewText = (
    data: ScheduleFormData,
    formattedTime: string,
    formattedDate: string,
    timezone: string,
    t: (key: string) => string
  ): string => {
    const repeatEvery = data.recurringRepeatEvery || 1;
    const dayText = repeatEvery === 1 ? t('day') : t('days');
    const repeatText = repeatEvery === 1 ? '' : `${repeatEvery} `;
    const startingDateText = formatStartingDateText(formattedDate, t);
    return `${t('Every')} ${repeatText}${dayText} ${t('at')} ${formattedTime}${startingDateText} (${timezone})`;
  };

  const generateWeeklyPreviewText = (
    data: ScheduleFormData,
    formattedTime: string,
    formattedDate: string,
    timezone: string,
    t: (key: string) => string
  ): string => {
    const days = data.recurringDays || [];
    const dayNames = getDayNamesMap(t);
    const selectedDayNames = days.map(d => dayNames[d] || d).join(', ');
    const startingDateText = formatStartingDateText(formattedDate, t);
    return `${t('Every')} ${selectedDayNames} ${t('at')} ${formattedTime}${startingDateText} (${timezone})`;
  };

  const getDayOfMonthText = (dayOfMonth: number | 'l' | undefined, t: (key: string) => string): string => {
    if (dayOfMonth === 'l') {
      return t('last day');
    }
    if (dayOfMonth === 1) {
      return t('first day');
    }
    if (dayOfMonth === 15) {
      return t('middle of month');
    }
    return `${t('day')} ${dayOfMonth}`;
  };

  const generateMonthlyPreviewText = (
    data: ScheduleFormData,
    formattedTime: string,
    timezone: string,
    t: (key: string) => string
  ): string => {
    const repeatEvery = data.recurringRepeatEvery || 1;
    const dayOfMonth = data.recurringDayOfMonth;
    const dayText = getDayOfMonthText(dayOfMonth, t);
    const monthText = repeatEvery === 1 ? t('month') : t('months');
    const repeatText = repeatEvery === 1 ? '' : `${repeatEvery} `;
    return `${t('Every')} ${repeatText}${monthText} ${t('on the')} ${dayText} ${t('at')} ${formattedTime} (${timezone})`;
  };

  const generateRecurringPreviewText = (data: ScheduleFormData, t: (key: string) => string): string => {
    const freq = data.recurringFrequency;
    const startTime = data.recurringStartTime;
    const startDate = data.recurringStartDate;
    const timezone = data.timezone || 'UTC';
    
    if (!startTime) {
      return t('Agent will execute based on the recurring schedule');
    }
    
    const formattedTime = formatTime24hTo12h(startTime);
    const formattedDate = startDate ? formatDateReadable(startDate) : '';
    
    if (freq === 'DAILY') {
      return generateDailyPreviewText(data, formattedTime, formattedDate, timezone, t);
    }
    
    if (freq === 'WEEKLY') {
      return generateWeeklyPreviewText(data, formattedTime, formattedDate, timezone, t);
    }
    
    if (freq === 'MONTHLY') {
      return generateMonthlyPreviewText(data, formattedTime, timezone, t);
    }
    
    return t('Agent will execute based on the recurring schedule');
  };

  export const generateSchedulePreviewText = (data: ScheduleFormData, t: (key: string) => string): string => {
    if (data.schedule_type === scheduleTypeOptions.RATE) {
      return generateRatePreviewText(data, t);
    }
    
    if (data.schedule_type === scheduleTypeOptions.One_time) {
      return generateOneTimePreviewText(data, t);
    }
    
    if (data.schedule_type === scheduleTypeOptions.Recurring) {
      return generateRecurringPreviewText(data, t);
    }
    
    return '';
  };

  export const processCustomParamsFromSchedule = (scheduleAny: any): Array<{ key: string; value: string }> => {
    if (scheduleAny.custom_params && typeof scheduleAny.custom_params === 'object' && Object.keys(scheduleAny.custom_params).length > 0) {
      const customParams = Object.entries(scheduleAny.custom_params)
        .filter(([key, value]) => key && value !== null && value !== undefined)
        .map(([key, value]) => ({
          key: String(key),
          value: String(value)
        }));
      return customParams.length > 0 ? customParams : [{ key: '', value: '' }];
    }
    return [{ key: '', value: '' }];
  };

  export const determineScheduleType = (simplifiedType: string | undefined): string => {
    if (simplifiedType === scheduleTypeOptions.RATE) {
      return scheduleTypeOptions.RATE;
    }
    if (simplifiedType === scheduleTypeOptions.Recurring) {
      return scheduleTypeOptions.Recurring;
    }
    if (simplifiedType === scheduleTypeOptions.One_time) {
      return scheduleTypeOptions.One_time;
    }
    return scheduleTypeOptions.RATE; // default
  };

  export const populateRateScheduleFields = (editValues: ScheduleFormData, expressionMetadata: any, scheduleAny?: any): void => {
    // API may return rate in schedule_rate or in pattern (type RATE)
    const rateSource = expressionMetadata?.schedule_rate ?? expressionMetadata?.pattern;
    if (rateSource && (rateSource.value !== undefined || rateSource.unit !== undefined)) {
      editValues.rateValue = rateSource.value ?? 1;
      editValues.rateUnit = rateSource.unit ?? 'hours';
    }
    if (scheduleAny) {
      if (scheduleAny.start_date) {
        editValues.rateStartDate = parseDate(scheduleAny.start_date);
      }
      if (scheduleAny.start_time && typeof scheduleAny.start_time === 'string') {
        editValues.rateStartTime = scheduleAny.start_time.slice(0, 5);
      }
      const endDate = scheduleAny.end_date;
      const isValidEndDate = endDate &&
        typeof endDate === 'string' &&
        endDate.trim().toLowerCase() !== 'nan' &&
        endDate.trim() !== '';
      if (isValidEndDate) {
        editValues.rateEndDate = parseDate(endDate);
        editValues.rateEndCondition = 'specific_date';
      } else {
        editValues.rateEndCondition = 'never';
      }
    }
  };

  export const populateRecurringScheduleFields = (editValues: ScheduleFormData, expressionMetadata: any, scheduleAny: any): void => {
    if (expressionMetadata?.pattern) {
      const pattern = expressionMetadata.pattern;
      editValues.recurringFrequency = pattern.type;
      editValues.recurringStartTime = scheduleAny?.start_time || pattern.time || '';
      
      if (pattern.type === 'WEEKLY' && pattern.days) {
        editValues.recurringDays = Array.isArray(pattern.days) ? pattern.days : [];
      } else if (pattern.type === 'MONTHLY' && pattern.day_of_month !== undefined) {
        editValues.recurringDayOfMonth = pattern.day_of_month === 'l' ? 'l' : Number(pattern.day_of_month);
      }
    }
    
    if (scheduleAny.start_date) {
      editValues.recurringStartDate = parseDate(scheduleAny.start_date);
    }
    // Check if end_date is valid (not null, undefined, empty string, or "nan")
    const endDate = scheduleAny.end_date;
    const isValidEndDate = endDate && 
      typeof endDate === 'string' && 
      endDate.trim().toLowerCase() !== 'nan' && 
      endDate.trim() !== '';
    if (isValidEndDate) {
      editValues.recurringEndDate = parseDate(endDate);
      editValues.recurringEndCondition = 'specific_date';
    } else {
      editValues.recurringEndCondition = 'never';
    }
  };

  export const populateOneTimeScheduleFields = (editValues: ScheduleFormData, expressionMetadata: any): void => {
    if (expressionMetadata?.schedule_at) {
      const scheduleAt = expressionMetadata.schedule_at;
      const dt = scheduleAt.split('T');
      editValues.oneTimeDate = dt[0] || '';
      editValues.oneTimeTime = dt[1]?.slice(0, 5) || '00:00';
      editValues.scheduleAt = scheduleAt.slice(0, 16);
    }
  };

  export const populateScheduleTypeFields = (editValues: ScheduleFormData, expressionMetadata: any, scheduleAny: any): void => {
    if (editValues.schedule_type === scheduleTypeOptions.RATE) {
      populateRateScheduleFields(editValues, expressionMetadata, scheduleAny);
    } else if (editValues.schedule_type === scheduleTypeOptions.Recurring) {
      populateRecurringScheduleFields(editValues, expressionMetadata, scheduleAny);
    } else if (editValues.schedule_type === scheduleTypeOptions.One_time) {
      populateOneTimeScheduleFields(editValues, expressionMetadata);
    }
  };
  